"use client";

/**
 * FloatOperationDisplay - CRUD operation results for floats
 */

import { useState } from "react";
import Link from "next/link";
import { Tag, Alert } from "@/ui";
import { spacing, textStyles } from "@/ui/tokens";
import { actionLinkClass } from "../utils";
import { Work, Visibility, Edit } from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  cleanErrorMessage,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface OperationOutput {
  success?: boolean;
  status?: "success" | "error" | "pending_confirmation";
  message?: string;
  float?: {
    id: string | number;
    name?: string;
    title?: string;
  };
  shareUrl?: string;
  urls?: {
    view?: string;
    edit?: string;
  };
  error?: string;
}

export function FloatOperationDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as OperationOutput | undefined;
  const isSuccess = result?.success === true || result?.status === "success";

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title=""
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Work style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded}>
        {isLoading && (
          <div style={{ padding: `${spacing[2]}px 0`, textAlign: "center" }}>
            <ToolProgress variant="dots" label="Processing..." />
          </div>
        )}

        {isError && error && <ToolError error={error} />}

        {!isLoading && result && (
          <Alert
            variant={isSuccess ? "success" : "error"}
            title={isSuccess ? "Success" : "Error"}
          >
            <span style={{ ...textStyles.body2 }}>
              {isSuccess
                ? (result.message || "Operation completed successfully")
                : cleanErrorMessage(result.message || result.error || "Operation failed")}
            </span>
            {result.float && (
              <div style={{ marginTop: spacing[2] }}>
                <Tag size="sm" color="neutral">
                  {result.float.name || result.float.title || `Float #${result.float.id}`}
                </Tag>
              </div>
            )}
            {(result.shareUrl || result.urls) && (
              <div style={{ marginTop: spacing[3], display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
                {result.urls?.view && (
                  <Link
                    href={result.urls.view}
                    className={actionLinkClass}
                  >
                    <Visibility style={{ fontSize: 14 }} /> View Float
                  </Link>
                )}
                {result.urls?.edit && (
                  <Link
                    href={result.urls.edit}
                    className={actionLinkClass}
                  >
                    <Edit style={{ fontSize: 14 }} /> Edit Float
                  </Link>
                )}
                {result.shareUrl && (
                  <Link
                    href={result.shareUrl}
                    className={actionLinkClass}
                  >
                    Share Link
                  </Link>
                )}
              </div>
            )}
          </Alert>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default FloatOperationDisplay;
